import './underwriting.bind.page.scss';
import { Alert, Box, Stack } from '@mui/material';
import {
  CircularLoader,
  ConfirmDialog,
  ConvergeSectionTitle,
  NewConvergeButton,
  PaperLayout,
} from '../../../presentation';
import {
  SubjectivityDeadline,
  SubmissionStatus,
  useCreateBinderLetterMutation,
  useGetBindPageQuery,
  useUpsertSubmissionMutation,
  useUpsertSubmissionSurplusLineMutation,
} from '@converge/graphql';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DocumentButton } from '../submission-action/document-button';
import { ISubmissionProps } from '../overview';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { UnderwritingBindOrPolicyHeader } from '../underwriting.bind-or-policy.header';
import { UnmetSubjectivitiesDialog } from '../underwriting.unmet-subjectivities-dialog';
import { UnderwritingSubjectivityList } from '../subjectivities';
import { UnderwritingSubmissionDetailPageSlug } from '@converge/data';
import { UnderwritingSubmissionPageContainer } from '../underwriting.submission.page.container';
import { base64ToPdfBlobUrl } from '../utils';
import { convergeLogger } from '@converge/config';
import { useConvergeSnackBar } from '../../../contexts';
import { useOutletContext } from 'react-router-dom';
import { useUnderwritingSubmissionDetailPage } from '../underwriting.submission.detail.hooks';
import { useUnderwritingSubmissionStateContext } from '../underwriting.submission.context';
import { SurplusLineHandle } from '../overview/underwriting.submission.surplusLine';

export const UnderwritingBindPage = () => {
  const [showBindConfirmDialog, setShowBindConfirmDialog] = useState(false);
  const [
    showHasUnmetSubjectivitiesDialog,
    setShowHasUnmetSubjectivitiesDialog,
  ] = useState(false);
  const [unmetSubjectivities, setUnmetSubjectivities] = useState<string[]>([]);
  const [isSubjectivitiesLoading, setIsSubjectivitiesLoading] = useState(false);
  const [isFetch, setIsFetch] = useState(false);
  const surplusLineRef = useRef<SurplusLineHandle>(null);
  const { navigate } = useUnderwritingSubmissionDetailPage();

  const { notify, closeSnackbar } = useConvergeSnackBar();
  const { createBinderLetter } = useCreateBinderLetterMutation();
  const { submission: submissionInfo } = useOutletContext<ISubmissionProps>();
  const {
    submission: { uuid: submissionUuid, status },
    info: { boundQuote },
  } = useUnderwritingSubmissionStateContext();

  const {
    upsertSubmission,
    loading: upsertSubmissionIsLoading,
    error: upsertSubmissionError,
  } = useUpsertSubmissionMutation();

  const { upsertSubmissionSurplusLine } =
    useUpsertSubmissionSurplusLineMutation();

  const autoBinderRegenerate = async () => {
    try {
      await createBinderLetter({
        variables: {
          submissionUuid,
        },
      }).then(() => {
        refetch();
        setIsFetch(false);
      });
      notify({
        message: 'Binder letter regenerated',
        variant: 'success',
      });
    } catch (error) {
      convergeLogger.error(error);
      notify(`Failed to regenerate binder letter:`, {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    if (upsertSubmissionError) {
      notify({
        message:
          status === SubmissionStatus.Quoted
            ? `Failed to bind submission: ${upsertSubmissionError.message}`
            : upsertSubmissionError.message,
        variant: 'error',
      });
    }
  }, [notify, upsertSubmissionError, isFetch, status]);

  useEffect(() => {
    if (isFetch === true) {
      autoBinderRegenerate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetch]);

  const {
    data,
    loading,
    error,
    info: { quote, subjectivities },
    refetch,
  } = useGetBindPageQuery({
    submissionUuid,
    quoteUuid: boundQuote?.uuid ?? '',
  });

  useEffect(() => {
    refetch();
  }, [refetch]);

  const handleBind = useCallback(async () => {
    setShowBindConfirmDialog(false);
    setShowHasUnmetSubjectivitiesDialog(false);
    const alert = notify({
      message:
        status === SubmissionStatus.Quoted
          ? 'Issuing binder...'
          : 'Unbinding...',
      variant: 'info',
    });

    if (surplusLineRef.current) {
      await upsertSubmissionSurplusLine({
        variables: {
          details: {
            uuid: submissionUuid,
            surplusLinesLicenseeInfo: surplusLineRef.current.getValues(),
          },
        },
      });
    }

    await upsertSubmission({
      variables: {
        details: {
          uuid: submissionUuid,
          status:
            status === SubmissionStatus.Quoted
              ? SubmissionStatus.Bound
              : SubmissionStatus.Quoted,
          subStatus: null,
        },
      },
    });

    closeSnackbar(alert);

    notify({
      message:
        status === SubmissionStatus.Quoted
          ? 'Binder issued successfully.'
          : 'Unbound.',
      variant: 'success',
    });
  }, [
    closeSnackbar,
    notify,
    status,
    submissionUuid,
    upsertSubmission,
    upsertSubmissionSurplusLine,
  ]);

  const validateBindWarning = useCallback(async () => {
    const showConfirmModal = () => {
      setShowBindConfirmDialog(true);
      setShowHasUnmetSubjectivitiesDialog(false);
    };

    const showUnmetSubjectivitiesModal = () => {
      setShowBindConfirmDialog(false);
      setShowHasUnmetSubjectivitiesDialog(true);
      setUnmetSubjectivities(subjectivitiesToBeWaivedOrSatisfied);
    };

    if (status === SubmissionStatus.Bound) {
      showConfirmModal();
      return;
    }
    // Only validate the surplus line form if the user can actually edit it. If they can edit it but it's incomplete, block the bind
    if (surplusLineRef.current && !surplusLineRef.current.isDisabled()) {
      const surplusLineValid = await surplusLineRef.current.triggerValidation();
      if (!surplusLineValid) {
        return;
      }
    }

    const subjectivitiesToBeWaivedOrSatisfied = subjectivities
      ?.filter(
        (s) =>
          s.quote?.deadline === SubjectivityDeadline.PriorToBind &&
          !s.quote?.waived &&
          !s.quote?.isSatisfied
      )
      .map((s) => {
        return s.name ?? '';
      });

    if (subjectivitiesToBeWaivedOrSatisfied.length > 0) {
      showUnmetSubjectivitiesModal();
    } else {
      showConfirmModal();
    }
  }, [status, subjectivities]);

  const pdfUrlQuote = useMemo(() => {
    return base64ToPdfBlobUrl(data?.quoteLetter.file);
  }, [data?.quoteLetter.file]);

  const pdfUrlBinder = useMemo(() => {
    return base64ToPdfBlobUrl(data?.binder.file);
  }, [data?.binder.file]);

  return (
    <UnderwritingSubmissionPageContainer
      title="Bind"
      onNext={
        status === SubmissionStatus.Bound
          ? () => {
              navigate.page({
                sectionUrl: UnderwritingSubmissionDetailPageSlug.Policy,
              });
            }
          : undefined
      }
      footerLeft={
        <Stack className="bind-footer">
          <Box className="button-box first-button-box">
            <NewConvergeButton
              disabled={!boundQuote}
              loading={upsertSubmissionIsLoading}
              label={
                status === SubmissionStatus.Quoted ? 'Issue binder' : 'Unbind'
              }
              onClick={() => validateBindWarning()}
            />
          </Box>
          {status &&
            [SubmissionStatus.Quoted, SubmissionStatus.Bound].includes(
              status
            ) && (
              <Box className="button-box">
                <RegenerateBinderLetterButton
                  isDisabled={!boundQuote}
                  submissionUuid={submissionUuid}
                  onCompleted={() => refetch()}
                />
              </Box>
            )}

          <Box className="button-box">
            {submissionInfo?.documentLink && (
              <DocumentButton
                submissionDocumentLink={submissionInfo.documentLink}
              />
            )}
          </Box>
          <ConfirmDialog
            show={showBindConfirmDialog}
            title={`${
              status === SubmissionStatus.Quoted ? 'Bind' : 'Unbind'
            } submission`}
            confirmButtonText="Yes"
            cancelButtonText="No"
            content={
              status === SubmissionStatus.Quoted
                ? 'Are you sure you want to issue this binder and bind the submission?'
                : 'Are you sure you want to unbind this submission and retract the binder?'
            }
            onCancel={() => setShowBindConfirmDialog(false)}
            onConfirm={handleBind}
          />
          <UnmetSubjectivitiesDialog
            open={showHasUnmetSubjectivitiesDialog}
            onClose={() => setShowHasUnmetSubjectivitiesDialog(false)}
            subjectivities={unmetSubjectivities}
          />
        </Stack>
      }
      onPrevious={() => {
        navigate.page({
          sectionUrl: UnderwritingSubmissionDetailPageSlug.Quote,
        });
      }}
    >
      <PaperLayout className="bind-container">
        <Stack>
          <UnderwritingBindOrPolicyHeader quote={quote} />
          <UnderwritingSubjectivityList
            subjectivities={subjectivities}
            submissionUuid={submissionUuid}
            quoteUuid={quote?.uuid ?? ''}
            setIsLoading={setIsSubjectivitiesLoading}
            isLoading={isSubjectivitiesLoading}
            deadline={SubjectivityDeadline.PriorToBind}
            setIsFetch={setIsFetch}
            isFetch={isFetch}
            surplusLineRef={surplusLineRef}
          />

          <ConvergeSectionTitle>Documents</ConvergeSectionTitle>
          <ConvergeSectionTitle variant="h5" mb={1}>
            Quote
          </ConvergeSectionTitle>
          {error ? (
            <Alert color="error" icon={<PriorityHighIcon />}>
              Error loading quote letter: {error.message}
            </Alert>
          ) : loading ? (
            <CircularLoader className="circular-loader-style" />
          ) : (
            data && (
              <iframe
                style={{
                  width: '100%',
                  height: '50vh',
                  flexGrow: 1,
                  margin: 0,
                  padding: 0,
                  border: '1px solid #eee',
                }}
                src={pdfUrlQuote}
                id="pdf"
                title="PDF"
              />
            )
          )}
          <br />
          <br />
          <ConvergeSectionTitle variant="h5" mb={1}>
            Binder
          </ConvergeSectionTitle>
          {error ? (
            <Alert color="error" icon={<PriorityHighIcon />}>
              Error loading binder: {error.message}
            </Alert>
          ) : loading ? (
            <CircularLoader className="circular-loader-style" />
          ) : (
            data && (
              <iframe
                style={{
                  width: '100%',
                  height: '50vh',
                  flexGrow: 1,
                  margin: 0,
                  padding: 0,
                  border: '1px solid #eee',
                }}
                src={pdfUrlBinder}
                id="pdf"
                title="PDF"
              ></iframe>
            )
          )}
        </Stack>
      </PaperLayout>
    </UnderwritingSubmissionPageContainer>
  );
};

const RegenerateBinderLetterButton = ({
  submissionUuid,
  onCompleted,
  isDisabled,
}: {
  submissionUuid: string;
  onCompleted?: () => void;
  isDisabled: boolean;
}) => {
  const { createBinderLetter, loading, error } =
    useCreateBinderLetterMutation();

  const { notify } = useConvergeSnackBar();

  useEffect(() => {
    if (error) {
      convergeLogger.error(error);
      notify(`Failed to regenerate binder letter: ${error.message}`, {
        variant: 'error',
      });
    }
  }, [error, notify]);

  return (
    <NewConvergeButton
      loading={loading}
      label="Regenerate binder letter"
      disabled={isDisabled}
      onClick={() => {
        createBinderLetter({
          variables: {
            submissionUuid,
          },
        }).then(() => {
          if (onCompleted) {
            onCompleted();
          }
        });
      }}
    />
  );
};

export default UnderwritingBindPage;
